import uplatformAuthBackground from '@/assets/auth/uplatformBackground.webp'
import ThanksImg from '@/assets/img/thanks.png'
import BalanceCoinsArt from '@/assets/tenants/uplatform/balance-coins-art.webp'
import uplatformCoinIcon from '@/assets/tenants/uplatform/coin.svg'
import CoinsBurnGiftImage from '@/assets/tenants/uplatform/coins-burn-notification-gift.webp'
import DecorationImage from '@/assets/tenants/uplatform/decoration-art.webp'
import DefaultAvatarImage from '@/assets/tenants/uplatform/default-avatar.webp'
import FAQDecorationImage from '@/assets/tenants/uplatform/faq-decoration-image.webp'
import FAQQuestionArt from '@/assets/tenants/uplatform/faq-question-art.webp'
import FeedbackMegaphoneArt from '@/assets/tenants/uplatform/feedback-megaphone-art.webp'
import LogoArt from '@/assets/tenants/uplatform/logo-art.svg'
import uplatformCoinsIcon from '@/assets/tenants/uplatform/multiCoins.svg'

import type { SemanticTokens, TenantTheme } from '../types'

export const uplatformTheme: TenantTheme = {
  colors: {
    Base: {
      White: '#ffffff',
      'White 1': '#f7e8ff',
      'White 2': '#f9f9f9',
      'White 3': '#e4e4e4',
      'White 4': '#fcfcfd',
      'White 5': '#f5f5f5',
      Black: '#414141',
      'Black 2': '#262626',
      'Dark Black': '#000000',
      'Dark Black Transparent': '#00000040',
      'Dark Black 2': '#020618',
      'Black Blue': '#262626',
      'Gray Blue': '#667085',
      'English Red': '#b90052',
      Transparent: '#ffffff',
    },

    Purple: {
      Default: '#6e00a3',
      '1': '#b92bff',
      '2': '#9c3dca',
      Light: '#c756ff',
      'Light 1': '#e09fff',
      Transparent: '#c756ff1a',
      'Transparent 40': '#c756ff66',
      'Transparent 70': '#c756ffb3',
      'Transparent 90': '#c756ffe6',
      '50': '#faf5ff',
      '100': '#f7e8ff',
      '200': '#e09fff',
      '300': '#c756ff',
      '400': '#b92bff',
      '500': '#9c3dca',
      '600': '#6e00a3',
      '700': '#5c0089',
      '800': '#4a006f',
      '900': '#380055',
      '950': '#1f002e',
    },

    Pink: {
      Default: '#b90052',
      Light: '#e70066',
      '50': '#fff0f6',
      '100': '#ffe0ed',
      '200': '#ffc0db',
      '300': '#ffa0c9',
      '400': '#ff66a3',
      '500': '#e70066',
      '600': '#b90052',
      '700': '#9a0044',
      '800': '#7a0036',
      '900': '#5a0028',
      '950': '#330016',
    },

    Brand: {
      primary: '#6E01A2',
      secondary: '#B265D7',
    },
  },

  semanticTokens: {
    text: {
      base: { value: '#020618', description: 'Base text color' },
      content: { value: '#414141', description: 'Content text color' },
      error: { value: 'var(--color-red-600)', description: 'Error text color' },
      'content-2': { value: '#BCBCBC', description: 'Lighten version of content text color' },
      inverse: { value: '#ffffff', description: 'Inverse text color' },
      primary: { value: '#4b4b4b', description: 'Primary text color' },
      secondary: { value: '#667085', description: 'Secondary text color' },
      tertiary: { value: '#818181', description: 'Tertiary text color' },
    },
    link: {
      default: { value: 'var(--purple-light)', description: 'Default link color' },
      hover: { value: '#9c3dca', description: 'Hover link color' },
      visited: { value: '#9c3dca', description: 'Visited link color' },
      active: { value: '#9c3dca', description: 'Active link color' },
      disabled: { value: '#9c3dca', description: 'Disabled link color' },
      focus: { value: '#9c3dca', description: 'Focus link color' },
      link: { value: '#9c3dca', description: 'Link color' },
      linkHover: { value: '#9c3dca', description: 'Link hover color' },
      linkVisited: { value: '#9c3dca', description: 'Link visited color' },
    },
    background: {
      'bg-brand': { value: '#6e01a2', description: 'Brand background color' },
      'bg-disabled': { value: 'var(--color-gray-200)', description: 'Disabled background color' },
      'bg-primary': { value: 'var(--grey)', description: 'Primary background color' },
      'bg-secondary': { value: '#e8e8e8', description: 'Secondary background color' },
      'bg-surface': { value: 'rgb(41 41 41 / 5%)', description: 'Surface background color' },
      'bg-surface-new': { value: '#496a7b12', description: 'New surface background color' },
      'bg-tertiary': { value: 'var(--color-base-black-blue)', description: 'Tertiary background color' },
      'bg-page': { value: 'var(--white)', description: 'Page background color' },
      'bg-table': { value: 'var(--color-base-white)', description: 'Table background color' },
      'bg-accent': { value: 'rgb(254 94 34 / 10%)', description: 'Pagination highlighted color' },
      'input-input-default': { value: '#ffffff', description: 'Default input background' },
    },
    border: {
      'border-disabled': { value: 'var(--color-gray-950)', description: 'Disabled border color' },
      'border-error': { value: 'var(--color-red-600)', description: 'Error border color' },
      'border-focus': { value: 'var(--color-purple-light)', description: 'Focus border color' },
      'border-primary': { value: 'var(--color-purple-light)', description: 'Primary border color' },
      'border-secondary': { value: 'var(--color-gray-300)', description: 'Secondary border color' },
    },
    button: {
      'button-border-radius': { value: '10px', description: 'Button border radius for medium/large' },
      'button-border-radius-small': { value: '5px', description: 'Button border radius for small/tiny' },
      'button-border-radius-full': { value: '999px', description: 'Button border radius full' },
      'button-disabled': { value: 'var(--color-gray-950)', description: 'Disabled button color' },
      'button-primary': { value: 'var(--orange)', description: 'Primary button color - Orange' },
      'button-color-primary': { value: 'var(--white)', description: 'Primary button color - Purple' },
      'button-primary-hover': {
        value: 'var(--purple)',
        description: 'Primary button hover color - Purple',
      },
      'button-secondary': { value: 'var(--grey)', description: 'Secondary button color - Light grey' },
      'button-color-secondary': { value: 'var(--black)', description: 'Secondary button color - Black' },
      'button-secondary-hover': {
        value: 'var(--dark-grey-transparent-40)',
        description: 'Secondary button hover color',
      },
      'button-tertiary': { value: 'rgb(41 41 41 / 10%)', description: 'Tertiary button color' },
      'button-tertiary-hover': { value: '#f3f3f326', description: 'Tertiary button hover color' },
      'button-outline': { value: 'transparent', description: 'Outline button background' },
      'button-ghost': { value: 'transparent', description: 'Ghost button background' },
    },
    common: {
      'primary-color-hover': { value: '#ffffff', description: 'Primary color hover state' },
      'primary-color-pressed': { value: '#ffffff', description: 'Primary color pressed state' },
      'primary-color-suppl': { value: '#ffffff', description: 'Primary color supplement' },
      'skeleton-color': { value: '#e0e0e0', description: 'Skeleton background color' },
      'success-color': { value: '#22C55E', description: 'Common success color' },
      'warning-color': { value: '#FB923C', description: 'Common warning color' },
    },
  } satisfies SemanticTokens,

  components: {
    common: {
      decorationImage: DecorationImage,
    },

    BaseDropdown: {
      outlinedNameBorderColor: 'var(--color-purple-300)',
      borderRadius: 'var(--border-radius-rounded-10)',
      boxShadowColor: 'var(--color-purple-transparent-40)',
      nameColor: 'var(--color-base-black)',
      nameColorHover: 'var(--color-purple-300)',
      nameBorderColorHover: 'var(--color-purple-300)',
      namePlaceholderColor: 'var(--text-secondary)',
      nameBackgroundColor: 'var(--color-base-white)',
      iconColor: 'var(--color-base-black)',
      iconColorHover: 'var(--color-purple-300)',
      menuBackgroundColor: 'var(--color-purple-300)',
      menuItemColor: 'var(--color-base-white-1)',
      menuItemBorderColor: 'var(--color-purple-600)',
      menuItemSelectedBackgroundColor: 'var(--color-purple-600)',
      menuItemSelectedColor: 'var(--text-inverse)',
      menuItemBackgroundColorHover: 'var(--color-purple-600)',
    },
    BaseCheckbox: {
      color: 'var(--text-content)',
      colorChecked: 'var(--orange)',
    },
    BaseShopList: {
      headingColor: 'var(--color-purple-default)',
    },
    BaseSortItem: {
      iconColor: 'var(--text-inverse)',
    },
    Pagination: {
      itemBackgroundColor: 'var(--background-bg-page)',
      itemColor: 'var(--text-content)',
      itemActiveBackgroundColor: 'var(--color-purple-light)',
      itemActiveColor: 'var(--text-inverse)',
    },
    SearchInput: {
      iconColor: 'var(--color-purple-light)',
      placeholderColor: 'var(--color-purple-light)',
      textColor: 'var(--color-purple-default)',
    },
    DynamicSearch: {
      backgroundColor: 'var(--color-purple-default)',
      searchPageBorderColor: 'var(--border-border-primary)',
      itemUserNameColor: 'var(--text-inverse)',
      itemUserPositionColor: 'var(--color-purple-light)',
      itemContainerBackgroundColorHover: 'var(--color-purple-light)',
      itemUserPositionColorHover: 'var(--text-inverse)',
    },

    header: {
      common: {
        backgroundColor: 'var(--purple)',
        borderBottom: 'none',
      },
      search: {
        backgroundColor: 'var(--purple)',
        borderColor: 'var(--border-border-primary)',
        placeholderColor: 'var(--text-inverse)',
        iconColor: 'var(--text-inverse)',
      },
      menu: {
        backgroundColor: 'var(--purple-light)',
        menuIconColor: 'var(--text-inverse)',
        menuColor: 'var(--text-inverse)',
        listBackgroundColor: 'var(--purple-light)',
        listItemColor: 'var(--text-inverse)',
      },
    },
    sidebar: {
      containerBackgroundColor: 'var(--background-bg-primary)',
      itemActiveBackgroundColor: 'var(--background-bg-page)',
      itemHoverBackgroundColor: 'var(--background-bg-page)',
      itemIconColor: 'var(--text-content)',
      itemIconActiveColor: 'var(--purple-light)',
      itemIconHoverColor: 'var(--purple-light)',
      groupColor: 'var(--text-secondary)',
      itemBackgroundColor: 'var(--purple-light)',
      itemColor: 'var(--text-content)',
    },

    home: {
      // Common
      // Faq
      cardFaqBackgroundColor: `var(--color-card-bg) url(${FAQQuestionArt})`,
      cardFaqColor: 'var(--text-content)',
      cardFaqLinkColor: 'var(--text-content)',
      // News
      cardNewsBackgroundColor: 'var(--orange)',
      cardNewsLinkColor: 'var(--text-inverse)',
      // Common
      cardCommonBorder: '1px solid var(--border-border-primary)',
      cardCommonLinkColor: 'var(--text-content)',
      cardCommonLinkIcon: {
        backgroundColor: '#6E00A3',
        arrowColor: 'var(--text-inverse)',
      },

      // User
      cardUserBackgroundColor: 'var(--background-bg-page)',
      cardUserNameColor: 'var(--text-content)',
      cardUserContactKeyColor: 'var(--text-secondary)',
      cardUserLinkColor: 'var(--text-content)',
      // Balance
      cardBalanceBackgroundColor: 'var(--background-bg-page)',
      cardBalanceBackgroundImage: `var(--color-primary) url(${BalanceCoinsArt}) top right / 146px 192px no-repeat`,
      cardBalanceColor: 'var(--pink)',
      cardBalanceHintColor: 'var(--color-pink-light)',
      cardBalanceLinkColor: 'var(--text-inverse)',
      cardBalanceCoinsIcon: uplatformCoinsIcon,
      coinsBurnGiftImage: CoinsBurnGiftImage,
      // Feedback
      cardFeedbackBackgroundColor: `var(--color-card-bg) url(${FeedbackMegaphoneArt})`,
      cardFeedbackLinkColor: 'var(--text-content)',
      // Events
      cardEventsBackgroundColor: 'var(--orange)',
      cardEventsLinkColor: 'var(--text-content)',
      // Birthdays
      cardBirthdaysBackgroundColor: 'var(--purple)',
      cardBirthdaysLinkColor: 'var(--text-inverse)',
      cardBirthdaysUserPositionColor: 'var(--purple-light)',
    },
    cart: {
      containerBackgroundColor: 'var(--background-bg-primary)',
      nameColor: 'var(--text-content)',
      detailsColor: 'var(--text-secondary)',
      priceColor: 'var(--text-content)',
    },
    shopItem: {
      nameColor: 'var(--text-content)',
      noItemsColor: 'var(--text-content)',
      priceColor: 'var(--text-content)',
      actionsColor: 'var(--text-secondary)',
      swiperNavigationColor: 'var(--purple-light)',
      buyCounterColor: 'var(--purple-light)',
      buyCounterBaseColor: 'var(--text-secondary)',

      balanceBadgeAmountColor: 'var(--purple-light)',
      cartBadgeIconColor: 'var(--purple-light)',
      attributeNameColor: 'var(--text-secondary)',
      attributeDescriptionColor: 'var(--text-content)',
      attributeValueBorderColor: 'var(--text-content)',
      attributeValueTextColor: 'var(--text-content)',
      attributeValueSelectedColor: 'var(--purple-light)',
      actionBarTitleColor: 'var(--text-content)',
      quantityControlColor: 'var(--text-content)',
      quantityCountColor: 'var(--text-content)',
    },
    tagsSelector: {
      tagBackgroundColor: 'transparent',
      tagBorderColor: 'var(--border-border-primary)',
      tagColor: 'var(--purple-light)',
      tagSelectedBackgroundColor: 'var(--purple-light)',
      tagSelectedBorderColor: 'transparent',
      tagSelectedColor: 'var(--text-inverse)',
    },
    lightbox: {
      slideBackgroundColor: 'var(--background-bg-page)',
      navigationColor: 'var(--purple-light)',
      closeButtonColor: 'var(--purple-light)',
    },
    shopGallery: {
      slideBackgroundColor: 'var(--background-bg-page)',
      thumbBackgroundColor: 'var(--background-bg-page)',
      navigationColor: 'var(--purple-light)',
    },
    createShopItem: {
      formWrapperBackgroundColor: 'var(--background-bg-primary)',
      formFieldBackgroundColor: 'var(--background-bg-page)',
      formFieldBorderColor: 'transparent',
      formFieldBorderColorFocus: 'transparent',
      formFieldColor: 'var(--text-content)',
      formFieldPlaceholderColor: 'var(--text-content)',
      formFieldLabelColor: 'var(--text-content)',
      formFieldIconColor: 'var(--text-content)',
      controlLinkColor: 'var(--purple-light)',
      addButtonColor: 'var(--purple-light)',
      editorBackgroundColor: 'var(--background-bg-page)',
      editorBorderColor: 'var(--border-border-secondary)',
      editorTextColor: 'var(--text-content)',
      editorControlColor: 'var(--text-content)',
      stockControlBackgroundColor: 'var(--background-bg-page)',
      stockControlBorderColor: 'var(--text-content)',
      stockControlHeadingColor: 'var(--text-content)',
      stockControlTextColor: 'var(--text-content)',
      stockControlScrollbarTrackColor: 'var(--purple-transparent-40)',
      stockControlScrollbarThumbColor: 'var(--purple-transparent-70)',
    },
    feedback: {
      formBackgroundColor: 'var(--background-bg-primary)',
      inputBackgroundColor: 'var(--background-bg-page)',
      inputBorderColor: 'transparent',
      inputTextColor: 'var(--text-content)',
      inputPlaceholderColor: 'var(--text-content)',
      dropdownMenuBackgroundColor: 'var(--purple-light)',
      dropdownMenuTextColor: 'var(--text-inverse)',
      submitButtonBackground: 'var(--button-button-primary)',
      submitButtonColor: 'var(--text-inverse)',
      cancelButtonBackground: 'var(--button-button-secondary)',
      cancelButtonColor: 'var(--text-content)',
      dropdownIsOutlined: false,
    },
    feedbackControl: {
      sectionBackgroundColor: 'var(--background-bg-primary)',
      sectionHeadingColor: 'var(--text-content)',
      inputBackgroundColor: 'var(--background-bg-page)',
      inputTextColor: 'var(--text-content)',
      inputPlaceholderColor: 'var(--text-secondary)',
      inputBorderColor: 'transparent',
      addLinkColor: 'var(--purple-light)',
      deleteLinkColor: 'var(--text-content)',
      deleteLinkColorHover: 'var(--purple-light)',
      counterColor: 'var(--orange)',
      scrollbarTrackColor: 'var(--background-bg-page)',
      scrollbarThumbColor: 'var(--text-secondary)',
    },
    badge: {
      coinIcon: uplatformCoinIcon,
    },
    balanceHistory: {
      containerBackgroundColor: 'var(--background-bg-primary)',
      infoBaseColor: 'var(--purple-light)',
      tabsContentColor: 'var(--purple-light)',
      infoDateColor: 'var(--purple-light)',
      infoPriceColor: 'var(--purple-light)',
      tabsIndicatorColor: 'var(--purple)',
    },
    baseDialog: {
      containerBackgroundColor: 'var(--purple)',
      textColor: 'var(--text-inverse)',
      confirmButtonBackgroundColor: 'var(--background-bg-page)',
      confirmButtonTextColor: 'var(--purple-light)',
      confirmButtonBackgroundColorHover: 'var(--button-button-primary)',
      cancelButtonBackgroundColor: 'var(--background-bg-page)',
      cancelButtonTextColor: 'var(--purple-light)',
      cancelButtonBackgroundColorHover: 'var(--button-button-primary)',
      cancelButtonTextColorHover: 'var(--text-inverse)',
      backdropBackgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    auth: {
      logoContainerBackground: uplatformAuthBackground,
    },
    profiles: {
      contentBackgroundColor: 'var(--background-bg-page)',
      titleColor: 'var(--purple)',
      lineKeyColor: 'var(--text-content)',
      profilesCardImage: LogoArt,
      profilesDefaultAvatarImage: DefaultAvatarImage,

      // Thanks dialog
      thanksDialogBackgroundColor: 'var(--background-bg-page)',
      thanksDialogImageBackground: `url(${ThanksImg}) no-repeat center / cover`,
      thanksDialogInputBackgroundColor: 'var(--background-bg-primary)',
      thanksDialogInputBorderColor: 'transparent',
      thanksDialogInputBorderColorFocus: 'transparent',
      thanksDialogInputColor: 'var(--text-content)',
      thanksDialogAmountInputColor: 'var(--color-pink-light)',
      thanksDialogCategoryInputListItemBackgroundColorHover: 'var(--color-purple-600)',
      thanksDialogSubmitButtonType: 'primary',
      thanksDialogCancelButtonType: 'secondary',
      thanksDialogFieldLabelColor: 'var(--text-content)',

      // ProfilesListItem
      profilesItemBackgroundColor: 'var(--background-bg-primary)',
      profilesItemNameColor: 'var(--text-content)',
      profilesItemDescriptionColor: 'var(--text-content)',

      // ProfilesListSearchBar
      profilesListSearchBarBorderColor: 'var(--border-border-primary)',
    },
    allocateCoins: {
      formFieldColor: 'var(--text-content)',
      formFieldLabelColor: 'var(--text-content)',
      formFieldPlaceholderColor: 'var(--text-content)',
      formFieldBorderColor: 'transparent',
      formFieldBorderColorFocus: 'transparent',
      formFieldBackgroundColor: 'var(--background-bg-page)',
      formFieldAmountCoinIcon: uplatformCoinsIcon,
      formFieldAmountCoinIconColor: 'var(--color-pink-light)',
      formFieldAmountColor: 'var(--color-pink-light)',
      formFieldCommentColor: 'var(--text-content)',
      formFieldNamePopupBackgroundColor: 'var(--background-bg-page)',
      formFieldNamePopupColor: 'var(--text-content)',
      formFieldNameInputColor: 'var(--text-content)',
      formFieldNameDropdownIconColor: 'var(--text-content)',
      formButtonSendType: 'primary',
      formButtonResetType: 'secondary',
      formFieldNameChipColor: 'var(--text-content)',
      formFieldNameChipBackgroundColor: 'var(--background-bg-primary)',
      formFieldNameChipRemoveColor: 'var(--color-purple-1)',
      formFieldCategoryItemBackgroundColorHover: 'var(--color-purple-default)',
      bulkModalOpenButtonType: 'secondary',
      tabsItemContentColor: 'var(--text-content-2)',
      tabsItemActiveContentColor: 'var(--text-primary)',
      tabsItemIndicatorColor: 'var(--background-bg-secondary)',
    },
    languageSwitcher: {
      textColor: 'var(--text-inverse)',
    },
    tagsControl: {
      sectionBackgroundColor: 'var(--background-bg-primary)',

      addItemButtonColor: 'var(--color-purple-1)',
    },
    orders: {
      paginationWrapperAfterBackgroundColor: 'transparent',

      // OrdersList
      listOrdersFilterPopupBackgroundColor: 'var(--background-bg-page)',
      listOrdersFilterPopupColor: 'var(--text-content)',
      listItemInfoBackgroundColor: 'var(--background-bg-primary)',
      listItemInfoNumberColor: 'var(--text-content)',
      listItemInfoReddyIconColorHover: 'var(--color-pink-light)',
      listItemInfoStateBackgroundColor: 'var(--background-bg-page)',
      listItemInfoStateColor: 'var(--text-content)',
      listItemInfoStateSelectedBackgroundColor: 'transparent',
      listItemInfoStateSelectedColor: 'inherit',
      listItemInfoStateMenuOptionsBackgroundColor: 'var(--color-purple-light)',
      listItemInfoStateMenuOptionBackgroundColorHover: 'var(--color-purple-default)',

      // OrderProduct
      orderProductInfoNameColor: 'var(--text-content)',
      orderProductInfoAttributesColor: 'var(--text-secondary)',
      orderProductInfoQuantityColor: 'var(--text-content)',
    },
    rolesControl: {
      actionsBarHeadingColor: 'var(--text-content)',
      actionsBarFiltersBackgroundColor: 'var(--background-bg-page)',
      actionsBarFiltersColorHover: 'var(--color-purple-300)',
      actionsBarFiltersMenuBackgroundColor: 'var(--component-base-dropdown-primary-color)',

      roleDropdownListColor: 'var(--text-inverse)',
      roleDropdownListItemBackgroundColorHover: 'var(--color-purple-light-1)',
      roleDropdownListItemSelectedBackgroundColor: 'var(--color-purple-default)',

      listItemBackgroundColor: 'var(--background-bg-primary)',
      listItemInfoNameColor: 'var(--text-content)',
      listItemRoleDropdownBackgroundColor: 'var(--background-bg-page)',
      listItemRoleDropdownColorHover: 'var(--color-purple-300)',
      listItemRoleDropdownMenuBackgroundColor: 'var(--component-base-dropdown-primary-color)',

      listPaginationWrapperAfterBackgroundColor: 'transparent',
    },
    // TODO: temporary solution, replace with common semantic tokens
    transactions: {
      borderPrimary: '#dfdfdf',
      filterBackground: '#fff',
      footerBackground: 'var(--background-bg-secondary)',
    },
    faq: {
      decorationImage: FAQDecorationImage,
    },
  },
}
